import { ToolInvokeOptions, ToolOutput, JSONSchema, JsonObject, InlineToolRunnable } from './types';

/**
 * Creates a tool with the given implementation, schema, and metadata.
 * This is a zod-free implementation that accepts JSON Schema directly and a validation function.
 *
 * @param func The function implementation for the tool
 * @param options Tool configuration options
 * @returns A tool object that can be used in the agent
 */
export function createTool<T>(
  func: (input: T, options: ToolInvokeOptions) => Promise<ToolOutput>,
  options: {
    name: string;
    description: string;
    inputSchema: JSONSchema; // JSON Schema directly - no zod dependency!
    validate: (input: JsonObject) => T; // Validation function - replaces zod.parse()
    metadata?: {
      explainer?: (input?: T) => string;
      [key: string]: any;
    };
    verboseParsingErrors?: boolean;
    responseFormat?: 'content_and_artifact' | string;
  }
): InlineToolRunnable {
  const { explainer, ...metadata } = options.metadata ?? {};
  const toolObj: InlineToolRunnable = {
    name: options.name,
    description: options.description,
    inputSchema: options.inputSchema,
    metadata: explainer
      ? {
          ...metadata,
          explainer: (input: JsonObject) => explainer(input as T),
        }
      : metadata,
    responseFormat: options.responseFormat,
    verboseParsingErrors: options.verboseParsingErrors,
    async invoke(input: JsonObject, invokeOptions: ToolInvokeOptions): Promise<ToolOutput> {
      try {
        if (invokeOptions.signal?.aborted) {
          throw new Error(`Tool ${options.name} execution was aborted`);
        }

        // Use the provided validation function instead of zod.parse()
        const parsedInput = options.validate(input);
        const functionPromise = func(parsedInput, invokeOptions);
        const promises = [functionPromise];

        if (invokeOptions.signal) {
          promises.push(
            new Promise((_, reject) => {
              invokeOptions.signal?.addEventListener(
                'abort',
                () => {
                  reject(new Error(`Tool ${options.name} execution was aborted`));
                },
                { once: true }
              );
            })
          );
        }

        // If we have a timeout, add a promise to handle it
        if (invokeOptions.timeout && invokeOptions.timeout > 0) {
          promises.push(
            new Promise((_, reject) => {
              setTimeout(() => {
                reject(new Error(`Tool ${options.name} execution timed out after ${invokeOptions.timeout}ms`));
              }, invokeOptions.timeout);
            })
          );
        }

        // Race the function promise against the abort and timeout promises
        return Promise.race(promises);
      } catch (error) {
        // Handle parsing errors and wrap them in ToolError
        if (options.verboseParsingErrors) {
          console.error(`Error invoking tool ${options.name}:`, error, input);
        }
        throw error;
      }
    },
  };

  return toolObj;
}
